import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider, ScrollRestoration } from 'react-router';
import { Registry } from './host/registry.ts';
import { pairPlugins, setupPlugins } from './host/load.ts';
import { createExtensionSlot, createPiletApi, Notifications, PageRoutes } from './host/react.tsx';
import { Frame, NotFound, notificationViews } from './shell/layout.tsx';
import { brand } from './shell/brand.ts';
import { modules, packages } from './plugins.ts';
import './style.css';

// The web platform: the frame, the plugin host and nothing else. Screens are
// plugins, so the `/` route is a screen plugin's.

// The app's name comes from one place; the document title is the first use.
document.title = brand.name;

const registry = new Registry({ brand });
const Extension = createExtensionSlot(registry);
const loaded = setupPlugins(pairPlugins(modules, packages), (meta) => createPiletApi(registry, meta, Extension));

if (import.meta.env.DEV) {
  // For checks from DevTools or a snapshot script: what set up and what failed.
  (window as unknown as { __host: unknown }).__host = { registry, loaded };
}

// One route catches every address; the pages plugins register are matched
// inside it (PageRoutes), so a page registered later, by a plugin added while
// the dev server runs, needs no new router. React Router's data router is
// used for `<ScrollRestoration />`: the window's scroll position is kept per
// history entry, so going back (the browser's button, or any `navigate(-1)`)
// returns to where the screen was left, and a new address starts at the top.
const App: React.FC = () => (
  <>
    <Frame Extension={Extension} notifications={<Notifications registry={registry} views={notificationViews} />}>
      <PageRoutes registry={registry} NotFound={NotFound} />
    </Frame>
    <ScrollRestoration />
  </>
);

const router = createBrowserRouter([{ path: '*', element: <App /> }]);

createRoot(document.querySelector('#app')!).render(<RouterProvider router={router} />);
